
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ProductCard from '../../components/ui/ProductCard';
import { banners, categories, featuredProducts, bestSellers } from '../../mocks/products';

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />

      {/* Hero Slider */}
      <section className="relative h-[500px] overflow-hidden">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-all duration-1000 ${
              index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            <div className="relative h-full">
              <img
                src={banner.image}
                alt={banner.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent">
                <div className="container mx-auto px-4 h-full flex items-center">
                  <div className={`max-w-2xl text-white transition-all duration-1000 delay-300 ${
                    index === currentSlide ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
                  }`}>
                    <h2 className="text-5xl font-bold mb-4 leading-tight">{banner.title}</h2>
                    <p className="text-xl mb-8 text-white/90">{banner.subtitle}</p>
                    <Link
                      to={banner.link}
                      className="inline-flex items-center gap-2 bg-[#FF8F00] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#F57C00] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
                    >
                      {banner.buttonText}
                      <i className="ri-arrow-left-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Slider Controls */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all cursor-pointer ${
                index === currentSlide
                  ? 'w-12 h-3 bg-white rounded-full'
                  : 'w-3 h-3 bg-white/50 rounded-full hover:bg-white/75'
              }`}
            />
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() => goToSlide((currentSlide - 1 + banners.length) % banners.length)}
          className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors z-10 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-white text-2xl"></i>
        </button>
        <button
          onClick={() => goToSlide((currentSlide + 1) % banners.length)}
          className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors z-10 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-white text-2xl"></i>
        </button>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">تسوق حسب القسم</h2>
            <p className="text-gray-600 text-lg">اختر من بين مجموعة واسعة من المنتجات والحلول</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/categories/${category.id}`}
                className={`group relative h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                  <div className="absolute bottom-0 right-0 left-0 p-6">
                    <div className="w-14 h-14 bg-[#FF8F00] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <i className={`${category.icon} text-white text-2xl`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{category.name}</h3>
                    <p className="text-white/80 text-sm mb-4">
                      {category.subcategories.length} فئة فرعية
                    </p>
                    <div className="flex items-center gap-2 text-white font-medium">
                      <span>تصفح الآن</span>
                      <i className="ri-arrow-left-line group-hover:translate-x-[-4px] transition-transform"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">المنتجات المميزة</h2>
              <p className="text-gray-600">أحدث المنتجات وأفضل العروض</p>
            </div>
            <Link
              to="/offers"
              className="flex items-center gap-2 text-[#0D47A1] font-medium hover:text-[#0a3a8a] cursor-pointer whitespace-nowrap"
            >
              عرض الكل
              <i className="ri-arrow-left-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.slice(0, 4).map((product, index) => (
              <div
                key={product.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <ProductCard {...product} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">الأكثر مبيعاً</h2>
              <p className="text-gray-600">المنتجات الأكثر طلباً من عملائنا</p>
            </div>
            <Link
              to="/categories"
              className="flex items-center gap-2 text-[#0D47A1] font-medium hover:text-[#0a3a8a] cursor-pointer whitespace-nowrap"
            >
              عرض الكل
              <i className="ri-arrow-left-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {bestSellers.slice(0, 4).map((product, index) => (
              <div
                key={product.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <ProductCard {...product} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-[#0D47A1] text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-truck-line text-3xl text-[#FF8F00]"></i>
              </div>
              <h3 className="font-bold text-lg mb-2">شحن سريع</h3>
              <p className="text-white/80 text-sm">توصيل لجميع مناطق المملكة</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-[#FF8F00]"></i>
              </div>
              <h3 className="font-bold text-lg mb-2">ضمان الجودة</h3>
              <p className="text-white/80 text-sm">منتجات أصلية 100%</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-3xl text-[#FF8F00]"></i>
              </div>
              <h3 className="font-bold text-lg mb-2">دعم فني</h3>
              <p className="text-white/80 text-sm">خدمة عملاء على مدار الساعة</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-refresh-line text-3xl text-[#FF8F00]"></i>
              </div>
              <h3 className="font-bold text-lg mb-2">سهولة الاسترجاع</h3>
              <p className="text-white/80 text-sm">إرجاع مجاني خلال 14 يوم</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#FF8F00] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4">هل لديك مشروع كبير؟</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            نقدم حلولاً متكاملة للمشاريع الكبرى من التصميم إلى التنفيذ والصيانة
          </p>
          <Link
            to="/quote-request"
            className="inline-flex items-center gap-2 bg-[#FF8F00] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#F57C00] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-list-3-line"></i>
            اطلب عرض سعر مجاني
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
